import React from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { CustomsParameterPage } from "@/pages/BackOffice/CustomsParameterPage";
import { TarifsPage } from "@/pages/BackOffice/TarifsPage";
import { OptionsParameterPage } from "@/pages/BackOffice/OptionsParameterPage";
import { GeneralInfoUsersParameterPage } from "@/pages/BackOffice/GeneralInfoUsersParameterPage";
import { GeneralRegisterParameterPage } from "@/pages/BackOffice/GeneralRegisterParameterPage";
import { LogPermissionsParameterPage } from "@/pages/BackOffice/LogPermissionsParameterPage";
import RegisterPage from "../pages/Auth/RegisterPage";
import Paths from "../constants/paths";
import LoginPage from "../pages/Auth/LoginPage";
import HomePage from "../pages/Home/HomePage";
import ForgotPasswordPage from "../pages/Auth/ForgotPasswordPage";
import ResetPasswordPage from "../pages/Auth/ResetPasswordPage";
import ForgotPasswordConfirmationPage from "../pages/Auth/ForgotPasswordConfirmationPage";
import ResetPasswordSuccessPage from "../pages/Auth/ResetPasswordSuccessPage";
import CotationsPage from "../pages/Home/Cotation/CotationsPage";
import { PendingShipmentsPage } from "../pages/Home/PendingShipments/PendingShipmentsPage";
import { ValidatedShipmentsPage } from "../pages/Home/ValidatedShipments/ValidatedShipmentsPage";
import { ProfilePage } from "../pages/Home/ProfilePage";
import { InvoicesPage } from "../pages/Home/InvoicesPage";
import { ProtectedRoute } from "./ProtectedRoute";
import { CotationsIdPage } from "../pages/Home/Cotation/CotationsIdPage";
import { UpdateQuotationManualPage } from "../pages/Home/PendingShipments/UpdateQuotationManualPage";
import { ProtectedRouteBo } from "./ProtectedRouteBo";
import { ClientsPage } from "../pages/BackOffice/ClientsPage";
import { ClientsIdPage } from "../pages/BackOffice/ClientsIdPage";
import { UpdateQuotationOcrPage } from "../pages/Home/PendingShipments/UpdateQuotationOcrPage";
import { CustomPage } from "../pages/BackOffice/CustomPage";
import { CustomCotationsPage } from "../pages/Home/Cotation/CustomCotationsPage";
import { CollectionPage } from "../pages/BackOffice/CollectionPage";
import { InvoicesBoPage } from "../pages/BackOffice/InvoicesBoPage";
import { ProtectedSubRouteBo } from "./ProtectedSubRouteBo";
import { CollectionParametersPage } from "../pages/BackOffice/CollectionParametersPage";
import { PostponePage } from "../pages/PostponePage";
import { TrackingPage } from "../pages/TrackingPage";
import { ProtectedSubRoute } from "./ProtectedSubRoute";
import { useAtomValue } from "jotai";
import { userAtom } from "@/stores/authStore";
import { CustomCotationsIdPage } from "@/pages/Home/Cotation/CustomCotationsIdPage";
import { UpdateRequestPage } from "@/pages/Home/ValidatedShipments/UpdateRequestPage";
import { ExpeditionsHistoryV2Page } from "@/pages/BackOffice/ExpeditionsHistoryV2Page";
import { ValidatedExpeditionsV2Page } from "@/pages/BackOffice/ValidatedExpeditionsV2Page";
import { DeletedExpeditionsV2Page } from "@/pages/BackOffice/DeletedExpeditionsV2Page";
import { BlockedExpeditionsV2Page } from "@/pages/BackOffice/BlockedExpeditionsV2Page";
import { ExpeditionsBoxPage } from "@/pages/BackOffice/ExpeditionsBoxPage";
import { ImporterPage } from "@/pages/BackOffice/ImporterPage";
import { ProductsPage } from "@/pages/BackOffice/ProductsPage";
import { ExpeditionFormPage } from "@/pages/BackOffice/ExpeditionFormPage";
import { RatingPage } from "@/pages/BackOffice/RatingPage";
import { CustomPolicyPage } from "@/pages/BackOffice/CustomPolicyPage";
import { CustomQuotationsBoxPage } from "@/pages/BackOffice/CustomQuotationsBoxPage";
import { LogsPage } from "@/pages/BackOffice/LogsPage";
import { PackagingPage } from "@/pages/BackOffice/PackagingPage";

const AppRoutes: React.FC = () => {
  const user = useAtomValue(userAtom);

  return (
    <Routes>
      <Route>
        <Route path={"/cgu.html"} />
        <Route path={Paths.auth.login} element={<LoginPage />} />
        <Route path={Paths.auth.register} element={<RegisterPage />} />
        <Route path={Paths.auth.forgotPassword} element={<ForgotPasswordPage />} />
        <Route
          path={Paths.auth.resetPasswordConfirmation}
          element={<ForgotPasswordConfirmationPage />}
        />
        <Route path={Paths.auth.resetPassword} element={<ResetPasswordPage />} />
        <Route path={Paths.auth.resetPasswordSuccess} element={<ResetPasswordSuccessPage />} />
      </Route>
      <Route>
        <Route path={Paths.others.postpone} element={<PostponePage />} />
        <Route path={Paths.others.tracking} element={<TrackingPage />} />
      </Route>

      {/* -------------------------------------------------------------- */}
      {/* Front Office routes */}
      {/* -------------------------------------------------------------- */}
      <Route element={<ProtectedRoute />}>
        <Route index element={<HomePage />} />
        <Route
          element={
            <ProtectedSubRoute
              role={user?.role || ""}
              allowedRoles={["ADMIN", "SUPERADMIN", "MEMBER"]}
            />
          }
        >
          <Route path={Paths.home.cotations} element={<CotationsPage />} />
          <Route path={Paths.home.customCotations} element={<CustomCotationsPage />} />
          <Route path={Paths.home.cotationsId} element={<CotationsIdPage />} />
          <Route path={Paths.home.customCotationsId} element={<CustomCotationsIdPage />} />
          <Route path={Paths.home.pendingShipments} element={<PendingShipmentsPage />} />
          <Route path={Paths.home.updateQuotationManual} element={<UpdateQuotationManualPage />} />
          <Route path={Paths.home.updateQuotationOcr} element={<UpdateQuotationOcrPage />} />
          <Route path={Paths.home.updateRequest} element={<UpdateRequestPage />} />
          <Route path={Paths.home.validatedShipments} element={<ValidatedShipmentsPage />} />
        </Route>
        <Route
          element={
            <ProtectedSubRoute role={user?.role || ""} allowedRoles={["ADMIN", "SUPERADMIN"]} />
          }
        >
          <Route path={Paths.home.profile} element={<ProfilePage />} />
        </Route>
        <Route
          element={
            <ProtectedSubRoute
              role={user?.role || ""}
              allowedRoles={["ADMIN", "SUPERADMIN", "ACCOUNTANT"]}
            />
          }
        >
          <Route path={Paths.home.invoices} element={<InvoicesPage />} />
        </Route>
      </Route>

      {/* -------------------------------------------------------------- */}
      {/* Back Office routes */}
      {/* -------------------------------------------------------------- */}
      <Route
        element={
          <ProtectedRouteBo
            isBoUser={user?.isBoUser || false}
            role={user?.role || null}
            isAuthenticated={!!user}
          />
        }
      >
        <Route
          element={
            <ProtectedSubRouteBo
              role={user?.role || ""}
              allowedRoles={["ADMIN", "SUPERADMIN", "MEMBER", "ACCOUNTANT"]}
            />
          }
        >
          <Route path={Paths.bo.clients} element={<ClientsPage />} />
          <Route path={Paths.bo.clientId} element={<ClientsIdPage />} />
          <Route path={Paths.bo.expeditionsHistoryV2} element={<ExpeditionsHistoryV2Page />} />
          <Route path={Paths.bo.validatedExpeditionsV2} element={<ValidatedExpeditionsV2Page />} />
          <Route path={Paths.bo.expeditionForm} element={<ExpeditionFormPage />} />
        </Route>
        <Route
          element={
            <ProtectedSubRouteBo
              role={user?.role || ""}
              allowedRoles={["ADMIN", "SUPERADMIN", "MEMBER"]}
            />
          }
        >
          <Route path={Paths.bo.expeditionsBox} element={<ExpeditionsBoxPage />} />
          <Route path={Paths.bo.ratings} element={<RatingPage />} />
          <Route path={Paths.bo.importer} element={<ImporterPage />} />
          <Route path={Paths.bo.products} element={<ProductsPage />} />
          <Route path={Paths.bo.blockedExpeditionsV2} element={<BlockedExpeditionsV2Page />} />
          <Route path={Paths.bo.deletedExpeditionsV2} element={<DeletedExpeditionsV2Page />} />
        </Route>
        <Route
          element={
            <ProtectedSubRouteBo
              role={user?.role || ""}
              allowedRoles={["ADMIN", "SUPERADMIN", "ACCOUNTANT"]}
            />
          }
        >
          <Route path={Paths.bo.invoices} element={<InvoicesBoPage />} />
        </Route>
        <Route
          element={
            <ProtectedSubRouteBo
              role={user?.role || ""}
              allowedRoles={["ADMIN", "SUPERADMIN", "PACKER", "MEMBER"]}
            />
          }
        >
          <Route path={Paths.bo.packaging} element={<PackagingPage />} />
        </Route>
        <Route
          element={
            <ProtectedSubRouteBo
              role={user?.role || ""}
              allowedRoles={["ADMIN", "SUPERADMIN", "DRIVER", "MEMBER"]}
            />
          }
        >
          <Route path={Paths.bo.collection} element={<CollectionPage />} />
        </Route>
        <Route
          element={
            <ProtectedSubRouteBo role={user?.role || ""} allowedRoles={["ADMIN", "SUPERADMIN"]} />
          }
        >
          <Route path={Paths.bo.customQuotationsBox} element={<CustomQuotationsBoxPage />} />
          <Route path={Paths.bo.customPolicy} element={<CustomPolicyPage />} />
        </Route>
        <Route
          element={
            <ProtectedSubRouteBo role={user?.role || ""} allowedRoles={["ADMIN", "SUPERADMIN"]} />
          }
        >
          <Route
            path={Paths.bo.parameters}
            element={<Navigate to={Paths.bo.parametersCustoms} replace />}
          />
          <Route path={Paths.bo.parametersCustoms} element={<CustomsParameterPage />} />
          <Route path={Paths.bo.customs} loader element={<CustomPage />} />
        </Route>
        <Route
          element={<ProtectedSubRouteBo role={user?.role || ""} allowedRoles={["SUPERADMIN"]} />}
        >
          <Route path={Paths.bo.parametersTarifs} element={<TarifsPage />} />
          <Route path={Paths.bo.parametersOptions} element={<OptionsParameterPage />} />
          <Route path={Paths.bo.parametersUsers} element={<GeneralInfoUsersParameterPage />} />
          <Route path={Paths.bo.parametersRegister} element={<GeneralRegisterParameterPage />} />
          <Route path={Paths.bo.parametersLogs} element={<LogPermissionsParameterPage />} />
          <Route path={Paths.bo.collectionParamters} element={<CollectionParametersPage />} />
          <Route path={Paths.bo.logs} element={<LogsPage />} />
        </Route>
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export default AppRoutes;
